// Centraliza las peticiones autenticadas, la renovación de sesión y el cierre de sesión.
import { clearAccessToken, getAccessToken, setAccessToken } from './tokenStore'
import { BASE_BACKEND_URL } from './apiConfig'

import { handleSessionError, parseErrorReason } from './sessionErrors'
import { requestAccessTokenFromPeers } from './authChannel'

let refreshPromise = null

const withCrossTabRefreshLock = async (callback) => {
  if (globalThis.navigator?.locks?.request) {
    return globalThis.navigator.locks.request('torre-auth-refresh', callback)
  }

  return callback()
}

const isAbortError = (error, signal) => {
  if (error?.name === 'AbortError') return true
  if (!signal?.aborted) return false

  return error === signal.reason || typeof error === 'string'
}

const getSessionRedirectReason = (originalReason, refreshReason) => {
  if (
    refreshReason === 'session_replaced' ||
    refreshReason === 'user_inactive' ||
    refreshReason === 'account_locked'
  ) {
    return refreshReason
  }

  return originalReason || refreshReason
}

async function requestAccessTokenRefresh() {
  let isCreator = false

  if (!refreshPromise) {
    isCreator = true
    const tokenBeforeLock = getAccessToken()
    refreshPromise = withCrossTabRefreshLock(async () => {
      try {
        const tokenAfterLock = getAccessToken()
        if (tokenAfterLock && tokenAfterLock !== tokenBeforeLock) {
          return { ok: true, token: tokenAfterLock }
        }

        // Una pestaña existente puede compartir su token vigente y evitar que
        // cada pestaña nueva rote por separado la cookie HttpOnly compartida.
        const peerToken = await requestAccessTokenFromPeers()
        if (peerToken?.token && peerToken.token !== tokenBeforeLock) {
          setAccessToken(peerToken.token, {
            broadcast: false,
            updatedAt: peerToken.updatedAt
          })
          return { ok: true, token: peerToken.token }
        }

        const response = await fetch(`${BASE_BACKEND_URL}/sessions/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          const reason = await parseErrorReason(response)

          const tokenAfterResponse = getAccessToken()
          if (tokenAfterResponse && tokenAfterResponse !== tokenBeforeLock) {
            return { ok: true, token: tokenAfterResponse }
          }

          const recoveredPeerToken = await requestAccessTokenFromPeers()
          if (recoveredPeerToken?.token && recoveredPeerToken.token !== tokenBeforeLock) {
            setAccessToken(recoveredPeerToken.token, {
              broadcast: false,
              updatedAt: recoveredPeerToken.updatedAt
            })
            return { ok: true, token: recoveredPeerToken.token }
          }

          clearAccessToken()
          return { ok: false, reason }
        }

        const data = await response.json()
        if (data.access_token) {
          setAccessToken(data.access_token)
        }
        return { ok: true, token: data.access_token || null }
      } catch {
        clearAccessToken()
        return { ok: false, reason: 'network_error' }
      }
    })
  }

  try {
    return await refreshPromise
  } finally {
    if (isCreator) {
      refreshPromise = null
    }
  }
}

export async function refreshAccessToken({ redirectOnFailure = true } = {}) {
  const result = await requestAccessTokenRefresh()

  if (!result.ok) {
    if (redirectOnFailure || result.reason === 'session_replaced') {
      handleSessionError(result.reason)
    }
    return null
  }

  if (!result.token) {
    clearAccessToken()
    return null
  }

  return result.token
}

export async function authFetchBinary(url, options = {}) {
  return authFetch(url, {
    ...options,
    headers: {
      Accept: '*/*',
      ...options.headers
    }
  })
}

export async function authFetch(url, options = {}) {
  const token = getAccessToken()
  const hasBody = options.body !== undefined && options.body !== null && options.body !== ''
  const headers = {
    Accept: 'application/json',
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers
  })

  if (response.status === 401) {
    const reason = await parseErrorReason(response.clone())

    if (!options._retry) {
      const latestToken = getAccessToken()
      if (latestToken && latestToken !== token) {
        return authFetch(url, { ...options, _retry: true })
      }

      if (
        reason === 'token_expired' ||
        reason === 'session_expired' ||
        reason === 'invalid_token' ||
        !reason
      ) {
        const refreshResult = await requestAccessTokenRefresh()
        if (refreshResult.ok && refreshResult.token) {
          return authFetch(url, { ...options, _retry: true })
        }

        clearAccessToken()
        handleSessionError(getSessionRedirectReason(reason, refreshResult.reason))
        return null
      }
    }

    clearAccessToken()
    handleSessionError(reason)
    return null
  }

  return response
}

export async function fetchWithAuth(url, options = {}) {
  try {
    const response = await authFetch(url, options)

    if (!response || response.status === 401) return null

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`)
    }

    if (response.status === 204 || response.status === 205) return null

    try {
      return await response.json()
    } catch (error) {
      if (error instanceof SyntaxError) return null
      throw error
    }
  } catch (error) {
    if (isAbortError(error, options.signal)) return null

    console.error('fetchWithAuth error:', error)
    throw error
  }
}

export async function signOut() {
  const token = getAccessToken()
  try {
    await fetch(`${BASE_BACKEND_URL}/users/sign_out`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Authorization: token ? `Bearer ${token}` : undefined
      }
    })
  } catch (e) {
    console.error('signOut error', e)
  }

  clearAccessToken({ broadcast: true })
}

export { getAccessToken, setAccessToken, clearAccessToken }
